import React from "react"
import PropTypes from "prop-types"
import RegistrationsPropTypes from "utils/prop_types"
import PaymentAmountSelect from "./payments/payment_amount_select"
import PaymentMethodSelect from "./payments/payment_method_select"
import StripeElementsForm from "components/stripe_elements_form"
import StripePaymentRequestButton from "components/stripe_payment_request_button"
import SubmitButton from "./payments/submit_button"
import ValidationMessage from "./payments/validation_message"
import SelectTestCard from "components/shared/select_test_card"

class PaymentForm extends React.Component {
  static propTypes = {
    createPaymentUrl: PropTypes.string.isRequired,
    currency: RegistrationsPropTypes.Currency.isRequired,
    finalized: PropTypes.bool.isRequired,
    minimumIsMet: PropTypes.bool.isRequired,
    paymentAmounts: PropTypes.arrayOf(RegistrationsPropTypes.PaymentAmount).isRequired,
    paymentMethods: PropTypes.arrayOf(RegistrationsPropTypes.PaymentMethod).isRequired
  }

  constructor(props) {
    super(props)

    const {
      allowMinimumDeposit,
      paymentAmounts,
      stripeAccountId,
      stripeEnabled,
      stripeKey
    } = this.props
    const nullPaymentAmount = {
      amountCents: 0,
      currency: paymentAmounts[0].currency,
      description: "default"
    }

    const defaultPaymentAmount =
      allowMinimumDeposit || paymentAmounts.length > 1 ? nullPaymentAmount : paymentAmounts[0]

    if (stripeEnabled) {
      this.stripe = Stripe(stripeKey, { stripeAccount: stripeAccountId })
    }

    this.state = {
      applePayAvailable: false,
      errors: null,
      paymentRequest: null,
      selectedPaymentAmount: defaultPaymentAmount,
      selectedPaymentMethod: props.paymentMethods[0],
      submitting: false
    }
  }

  componentDidMount() {
    this.createPaymentRequest()
  }

  render() {
    const {
      allowMinimumDeposit,
      buttonClasses,
      currency,
      organizationName,
      paymentAmounts,
      stripeAccountId,
      stripeEnabled,
      stripeTestmode,
      stripeTestmodeMessageDisabled,
      stripeKey,
      registrationIsFinalized
    } = this.props

    const {
      errors,
      paymentRequest,
      selectedPaymentAmount,
      selectedPaymentMethod,
      submitting,
      showingPaymentForm = !registrationIsFinalized
    } = this.state

    const { isoCode: currencyIsoCode } = currency
    const { amountCents } = selectedPaymentAmount
    const maxAmountCents = this.getMaximumAmountCents()
    const minAmountCents = this.getMinimumAmountCents()
    const noop = () => {}

    return (
      <>
        {showingPaymentForm && (
          <div>
            <PaymentMethodSelect
              paymentMethods={this.getPaymentMethods()}
              onChange={this.handlePaymentMethodSelect}
              selectedPaymentMethod={selectedPaymentMethod}
            />

            {this.shouldShowCreditCardForm() && (
              <StripeElementsForm
                {...{
                  amountCents,
                  currencyIsoCode,
                  organizationName,
                  stripeAccountId,
                  stripeEnabled,
                  stripeKey
                }}
                hideFields={false}
                onError={this.handleStripeError}
                onSuccess={this.handleStripePayment}
                render={stripe => {
                  this.handleCreditCardPayment = () => {
                    this.setState({ errors: null, submitting: true }, stripe.createToken())
                  }

                  return (
                    <div>
                      {errors && <div className="user-message error-alert alert">{errors}</div>}
                      {stripeTestmode && !stripeTestmodeMessageDisabled && <SelectTestCard />}
                      <div className="stripe-fields mb-3">{stripe.formFields}</div>
                    </div>
                  )
                }}
              />
            )}

            {this.shouldShowAmount() && (
              <div>
                <PaymentAmountSelect
                  {...{
                    allowMinimumDeposit,
                    paymentAmounts,
                    selectedPaymentAmount
                  }}
                  onChange={this.handlePaymentAmountSelect}
                />

                {this.shouldShowAmountWarning() && (
                  <ValidationMessage {...{ currency, maxAmountCents, minAmountCents }} />
                )}
              </div>
            )}

            {this.shouldShowApplePayButton() ? (
              <StripePaymentRequestButton
                amountCents={amountCents}
                label={organizationName}
                stripe={this.stripe}
                paymentRequest={paymentRequest}
                onSuccess={this.handleStripePayment}
                render={stripe => <div className="payment-buttons">{stripe.payButton}</div>}
              />
            ) : (
              <div className="my-4 ta-c">
                <SubmitButton
                  buttonClasses={`btn ${submitting && "disabled-btn"}`}
                  buttonLabel={submitting ? "Please Wait" : this.getSubmitText()}
                  disabled={this.isSubmitDisabled()}
                  onClick={this.isSubmitDisabled() ? noop : this.handleSubmit}
                />
              </div>
            )}
          </div>
        )}
        {this.shouldShowCreditCardForm() && !showingPaymentForm && (
          <div className="ta-c my-3">
            <button className="btn" onClick={() => this.setState({ showingPaymentForm: true })}>
              Make payment
            </button>
          </div>
        )}
      </>
    )
  }

  createPaymentRequest = () => {
    const {
      stripeEnabled,
      organizationName,
      currency: { isoCode }
    } = this.props
    const { selectedPaymentAmount } = this.state
    const { amountCents } = selectedPaymentAmount

    if (stripeEnabled) {
      const paymentRequest = this.stripe.paymentRequest({
        country: "US",
        currency: isoCode.toLowerCase(),
        total: {
          label: organizationName,
          amount: amountCents
        }
      })

      paymentRequest.canMakePayment().then(result => {
        if (result !== null && result.applePay) {
          this.setState({ applePayAvailable: true, paymentRequest })
        }
      })
    }
  }

  getMaximumAmountCents = () => {
    const { paymentAmounts } = this.props
    return Math.max(...paymentAmounts.map(({ amountCents }) => amountCents))
  }

  getMinimumAmountCents = () => {
    const { minimumIsMet, paymentAmounts, selectedPaymentMethod } = this.props

    if (selectedPaymentMethod != null && selectedPaymentMethod.id === "pay_later") {
      return 0
    } else if (minimumIsMet) {
      return 50
    } else {
      return Math.min(...paymentAmounts.map(({ amountCents }) => amountCents))
    }
  }

  getPaymentMethods = () => {
    const { paymentMethods } = this.props
    const { applePayAvailable } = this.state
    let additionalPaymentMethods = []

    if (applePayAvailable) {
      additionalPaymentMethods.push({
        id: "apple",
        description: "Apple Pay",
        requireAmount: true
      })
    }

    return [].concat(
      paymentMethods.slice(0, 1),
      additionalPaymentMethods,
      paymentMethods.slice(1, paymentMethods.length)
    )
  }

  getSubmitText = () => {
    const { finalized } = this.props
    return finalized ? "Make payment" : "Register"
  }

  handleFormCompletion = () => {
    const { finalizeUrl } = this.props
    window.location = finalizeUrl
  }

  handlePaymentAmountSelect = paymentAmount => {
    const { organizationName, stripeEnabled } = this.props
    const {
      paymentRequest,
      selectedPaymentAmount: { amountCents: previousAmountCents }
    } = this.state

    this.setState({ selectedPaymentAmount: paymentAmount }, () => {
      const {
        selectedPaymentAmount: { amountCents }
      } = this.state

      if (stripeEnabled && paymentRequest && previousAmountCents != amountCents) {
        paymentRequest.update({
          total: {
            amount: amountCents,
            label: organizationName
          }
        })
      }
    })
  }

  handlePaymentMethodSelect = paymentMethod => {
    this.setState({ selectedPaymentMethod: paymentMethod })
  }

  handleStripeError = error => {
    this.setState({ submitting: false })
  }

  handleStripePayment = (stripeEvent, callback) => {
    const { createPaymentUrl, idempotencyKey } = this.props
    const {
      selectedPaymentAmount: { amountCents }
    } = this.state

    let bodyBlob = {
      stripe_token: stripeEvent.token ? stripeEvent.token.id : stripeEvent.id,
      idempotency_key: idempotencyKey,
      amount_cents: amountCents
    }

    if (window.location.href.match(/source=([^&]+)/)) {
      bodyBlob["source"] = RegExp.$1
    }

    fetch(createPaymentUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": Rails.csrfToken()
      },
      credentials: "include",
      body: new Blob([JSON.stringify(bodyBlob)])
    })
      .then(resp => resp.json())
      .then(resp => {
        if (callback !== undefined) {
          callback(resp)
        }

        if (resp.errors) {
          stripeEvent.complete && stripeEvent.complete("fail")
          this.setState({ submitting: false, errors: resp.errors })
        } else {
          stripeEvent.complete && stripeEvent.complete("success")
          Turbolinks.visit(resp.url)
        }
      })
      .catch(resp => {
        stripeEvent.complete && stripeEvent.complete("fail")
        Bugsnag.notifyException(resp, "CreatePaymentFailure")
      })
  }

  handleSubmit = () => {
    const {
      selectedPaymentAmount: { amountCents },
      selectedPaymentMethod: { id }
    } = this.state

    if (amountCents <= 0) {
      this.handleFormCompletion()
    } else {
      switch (id) {
        case "credit":
          this.handleCreditCardPayment()
      }
    }
  }

  isAmountInBounds = () => {
    const {
      selectedPaymentAmount: { amountCents }
    } = this.state
    const maxAmountCents = this.getMaximumAmountCents()
    const minAmountCents = this.getMinimumAmountCents()

    return minAmountCents <= amountCents && amountCents <= maxAmountCents
  }

  isSubmitDisabled = () => {
    const { selectedPaymentMethod } = this.state
    return selectedPaymentMethod.id !== "pay_later" && !this.isAmountInBounds()
  }

  shouldShowAmount = () => {
    const {
      selectedPaymentMethod: { requireAmount }
    } = this.state
    return requireAmount
  }

  shouldShowAmountWarning = () => {
    const {
      selectedPaymentAmount: { description }
    } = this.state
    return description != "default" && !this.isAmountInBounds()
  }

  shouldShowApplePayButton = () => {
    const {
      selectedPaymentAmount: { amountCents },
      selectedPaymentMethod: { id }
    } = this.state

    return id === "apple" && amountCents > 0
  }

  shouldShowCreditCardForm = () => {
    const {
      selectedPaymentMethod: { id }
    } = this.state
    return id === "credit"
  }
}

export default PaymentForm
